import { statusDotStyles } from "@/components/feature/StatusBadge";
import ScrollFade from "@/components/base/ScrollFade";
import ScoreBadge from "@/components/feature/ScoreBadge";
import { pipelineStages, sideStages, stageHints } from "@/mocks/prospectos";
import type { Prospect } from "@/pages/prospectar/components/ProspectCard";

type ContactBoardViewProps = {
  contacts: Prospect[];
  onMove: (id: string, status: string) => void;
  onOpen: (c: Prospect) => void;
  dragId: string | null;
  setDragId: (id: string | null) => void;
  overStage: string | null;
  setOverStage: (stage: string | null) => void;
};

export default function ContactBoardView({
  contacts,
  onMove,
  onOpen,
  dragId,
  setDragId,
  overStage,
  setOverStage,
}: ContactBoardViewProps) {
  return (
    <ScrollFade
      containerClassName="flex gap-4 overflow-x-auto pb-3 vv-board-scroll snap-x snap-proximity scroll-px-1"
      ariaLabel="Tablero de contactos por etapas"
      hint="Desliza para ver más etapas"
      hintSessionKey="vv-crm-board-hint"
    >
      {[...pipelineStages, ...sideStages].map((stage) => {
        const items = contacts.filter((c) => c.status === stage);
        const isOver = overStage === stage;
        return (
          <div
            key={stage}
            onDragOver={(e) => {
              e.preventDefault();
              setOverStage(stage);
            }}
            onDragLeave={() => setOverStage(overStage === stage ? null : overStage)}
            onDrop={(e) => {
              e.preventDefault();
              if (dragId) onMove(dragId, stage);
              setDragId(null);
              setOverStage(null);
            }}
            className={[
              "w-[280px] flex-shrink-0 snap-start rounded-2xl border p-3 transition-colors",
              isOver ? "border-primary-400 bg-primary-500/5" : "border-background-200 bg-background-200",
            ].join(" ")}
          >
            <div className="flex items-center justify-between mb-3 px-1">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${statusDotStyles[stage] ?? "bg-background-400"}`}></span>
                <span className="font-heading font-bold text-sm text-foreground-950" title={stageHints[stage] ?? ""}>{stage}</span>
              </div>
              <span className="text-xs font-semibold text-foreground-500 bg-background-50 rounded-full px-2 py-0.5">
                {items.length}
              </span>
            </div>

            <div className="space-y-2 min-h-[80px]">
              {items.map((c) => (
                <div
                  key={c.id}
                  draggable
                  role="button"
                  tabIndex={0}
                  onDragStart={() => setDragId(c.id)}
                  onDragEnd={() => {
                    setDragId(null);
                    setOverStage(null);
                  }}
                  onClick={() => onOpen(c)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") onOpen(c);
                  }}
                  className={[
                    "w-full text-left bg-background-100 border border-background-200 rounded-xl p-3 cursor-grab active:cursor-grabbing hover:border-primary-400 transition-colors",
                    dragId === c.id ? "opacity-50" : "",
                  ].join(" ")}
                >
                  <div className="flex items-center gap-2.5">
                    <img
                      src={c.photo}
                      alt={`Foto de ${c.name}`}
                      className="w-9 h-9 rounded-lg object-cover object-top border border-background-200 flex-shrink-0"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-sm text-foreground-950 truncate">{c.name}</div>
                      <div className="text-[11px] text-foreground-500 truncate">{c.username}</div>
                    </div>
                    <ScoreBadge score={c.score} />
                  </div>
                  {c.needsHuman ? (
                    <div className="mt-2 flex items-center gap-1.5 text-[11px] font-semibold text-accent-700 bg-accent-500/10 rounded-lg px-2 py-1">
                      <i className="ri-alarm-warning-line"></i>
                      <span className="truncate">{c.needsHumanReason ?? "Necesita tu intervención"}</span>
                    </div>
                  ) : null}
                  <div className="mt-2 flex items-center gap-1.5 text-[11px] text-foreground-600">
                    <i className="ri-arrow-right-circle-line text-primary-500"></i>
                    <span className="truncate">{c.nextAction}</span>
                  </div>
                  <div
                    className="lg:hidden mt-2 flex items-center gap-2 bg-background-100 border border-background-200 rounded-lg px-2 py-1.5"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <i className="ri-drag-move-2-line text-foreground-500 text-sm"></i>
                    <span className="text-[11px] text-foreground-500 whitespace-nowrap">Etapa:</span>
                    <select
                      value={c.status}
                      onChange={(e) => onMove(c.id, e.target.value)}
                      aria-label="Cambiar etapa del contacto"
                      className="flex-1 min-w-0 bg-transparent outline-none text-[11px] font-semibold text-foreground-950 cursor-pointer"
                    >
                      {[...pipelineStages, ...sideStages].map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}

              {items.length === 0 ? (
                <div className="border border-dashed border-background-300 rounded-xl py-6 text-center text-[11px] text-foreground-400">
                  Arrastra aquí una tarjeta
                </div>
              ) : null}
            </div>
          </div>
        );
      })}
    </ScrollFade>
  );
}